import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

/* Highlights unfilled placeholder text so it's visually obvious before publication */
const Placeholder = ({ label }) => (
    <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs font-mono">[{label}]</span>
);

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Privacy Policy | Sampler</title>
                <meta name="description" content="Sampler Privacy Policy — Ritson Portfolio Ltd." />
            </Helmet>

            {/* Sticky header */}
            <div className="sticky top-0 z-10 border-b border-purple-100 bg-white/80 backdrop-blur-md">
                <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
                    <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 hover:text-[#8B5CF6] -ml-2" data-testid="legal-back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                    <img src="/sampler-logo.png" alt="Sampler" className="h-8 w-auto" />
                </div>
            </div>

            {/* Document body */}
            <div className="max-w-3xl mx-auto px-4 py-12">
                <div className="bg-white rounded-3xl border border-purple-200/50 shadow-sm p-8 md:p-12">

                    {/* Document header */}
                    <div className="mb-10 pb-8 border-b border-purple-100">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Legal
                        </span>
                        <h1 className="text-3xl font-bold mb-5 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Privacy Policy
                        </h1>
                        <div className="text-sm text-slate-500 space-y-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            <p>Ritson Portfolio Ltd (trading as Sampler) | Company Number: 17242741 | Registered in England and Wales</p>
                            <p>Registered Office: Ritson Haven, Ritson Road, London, E8 1DE</p>
                            <p>ICO Registration Number: ZC195328</p>
                            <p className="font-semibold text-slate-700 mt-2">Effective Date: 13 July 2026</p>
                        </div>
                    </div>

                    <div className="space-y-10 text-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }}>

                        {/* 1 */}
                        <Section title="1. Introduction">
                            <Clause>1.1 This Privacy Policy explains how Ritson Portfolio Ltd (trading as Sampler) (the "Company", "we", "us", "our"), collects, uses, stores, and processes personal data when you use our platform (Platform).</Clause>
                            <Clause>1.2 We are committed to protecting your personal data and complying with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</Clause>
                            <Clause>1.3 This policy applies to:</Clause>
                            <BulletList items={[
                                'Candidates using the Platform;',
                                'Employers and Clients using the Platform; and',
                                'visitors to our website.'
                            ]} />
                        </Section>

                        {/* 2 */}
                        <Section title="2. Data We Collect">
                            <BoldClause heading="2.1 Identity and Contact Data">
                                Name, email address, phone number, and location.
                            </BoldClause>
                            <BoldClause heading="2.2 Professional Information">
                                Employment history, skills and experience, CVs and uploaded documents, and LinkedIn or other professional profiles.
                            </BoldClause>
                            <BoldClause heading="2.3 Assessment and Performance Data">
                                Responses to assessments and microtests; time taken to complete tasks and behavioural signals; AI-generated scores and insights across six dimensions: Problem Solving, Execution and Delivery, Learning Speed, Ownership and Initiative, Collaboration, and Communication; Talent Card data and summaries; post-trial employer feedback, including any exceptional performance signals recorded by Clients.
                            </BoldClause>
                            <BoldClause heading="2.4 Platform Usage Data">
                                Interactions with the Platform; trial participation and outcomes; employer feedback following trials; and engagement metrics.
                            </BoldClause>
                            <BoldClause heading="2.5 Payment and Transaction Data">
                                Payment records relating to trial shifts, Conversion Fee triggers, and payment status (via Stripe). We do not store full payment card details.
                            </BoldClause>
                            <BoldClause heading="2.6 Technical Data">
                                IP address, browser type, device information, cookies, and usage analytics.
                            </BoldClause>
                        </Section>

                        {/* 3 */}
                        <Section title="3. How We Use Your Data">
                            <BoldClause heading="3.1 Platform Functionality">
                                Create and manage user accounts; enable candidate-employer matching; facilitate trial shift bookings.
                            </BoldClause>
                            <BoldClause heading="3.2 Talent Evaluation and AI Processing">
                                Generate Talent Cards; produce AI-driven scores and summaries; analyse performance across key dimensions; improve matching accuracy.
                            </BoldClause>
                            <BoldClause heading="3.3 Employer Matching and Hiring">
                                Display candidate profiles to relevant employers; enable employers to compare candidates; facilitate offer communication and hiring.
                            </BoldClause>
                            <BoldClause heading="3.4 Payment Processing">
                                Facilitate trial payments and refunds; process Conversion Fees; manage financial transactions via Stripe.
                            </BoldClause>
                            <BoldClause heading="3.5 Communication">
                                Send transactional emails (e.g. trial booking confirmations, reminders, offers) via Resend; provide platform updates and notifications; respond to support enquiries.
                            </BoldClause>
                            <BoldClause heading="3.6 Platform Improvement">
                                Analyse usage trends; improve AI models and matching systems; enhance user experience.
                            </BoldClause>
                        </Section>

                        {/* 4 */}
                        <Section title="4. Legal Basis for Processing">
                            <Clause>4.1 We rely on the following legal bases:</Clause>
                            <BulletList items={[
                                <><strong>Contractual necessity</strong> — to provide the Platform services you have signed up for;</>,
                                <><strong>Legitimate interests</strong> — to improve and operate the Platform, prevent fraud, and maintain security;</>,
                                <><strong>Consent</strong> — where required, such as for marketing communications and certain data sharing;</>,
                                <><strong>Legal obligation</strong> — where we are required to process data by law.</>
                            ]} />
                        </Section>

                        {/* 5 */}
                        <Section title="5. Automated Decision-Making and AI (UK GDPR Article 22)">
                            <Clause>5.1 The Platform uses automated systems to generate scores and performance insights, assist in candidate ranking and matching, and produce AI-driven summaries for Talent Cards.</Clause>
                            <Clause>5.2 These systems are designed to support, not replace, human decision-making. No decision with a legal or similarly significant effect is made solely by automated means without human involvement.</Clause>
                            <Clause>5.3 Employers make all final hiring decisions. The Company does not make hiring decisions on behalf of any party.</Clause>
                            <Clause>5.4 Candidates have the right to:</Clause>
                            <BulletList items={[
                                'request meaningful information about how their automated score or Talent Card was generated;',
                                'request human review of any automated result that has a significant effect on their profile;',
                                'object to processing based solely on automated means where it produces significant effects.'
                            ]} />
                            <Clause>5.5 AI-generated scores are retained for the duration of the Candidate's active account and for up to 12 months following account deletion, after which they will be anonymised or deleted. The legal basis for retaining scores post-deletion is legitimate interest in maintaining the integrity of the Platform's matching data.</Clause>
                            <Clause>5.6 Requests relating to automated processing may be submitted to <a href="mailto:privacy@sampler.org.uk" className="text-[#8B5CF6] hover:underline">privacy@sampler.org.uk</a>. We will respond within 30 days.</Clause>
                        </Section>

                        {/* 6 */}
                        <Section title="6. Data Sharing">
                            <BoldClause heading="6.1 With Employers (Core Marketplace Function)">
                                Candidate Talent Card information, assessment results, AI summaries, and profile and professional information may be shared with Clients as part of the core marketplace function.
                            </BoldClause>
                            <BoldClause heading="6.2 Post-Offer Data Sharing">
                                Once a Candidate accepts an offer, their personal contact details are shared with the Employer to enable contract issuance and onboarding. This occurs only after explicit Candidate consent via offer acceptance on the Platform.
                            </BoldClause>
                            <Clause>6.3 <strong>Service Providers</strong> — We share data with trusted third-party service providers, including:</Clause>
                            <BulletList items={[
                                'Stripe — payment processing;',
                                'Resend — transactional email delivery;',
                                'Hosting and infrastructure providers.'
                            ]} />
                            <Clause>All service providers are required to process data only in accordance with our instructions and in compliance with UK GDPR.</Clause>
                            <BoldClause heading="6.4 Legal Requirements">
                                We may disclose data where required to comply with a legal obligation, court order, or to protect the rights, property, or safety of the Company, its users, or others.
                            </BoldClause>
                        </Section>

                        {/* 7 */}
                        <Section title="7. Data Retention">
                            <Clause>7.1 We retain personal data:</Clause>
                            <BulletList items={[
                                'for as long as your account is active;',
                                'as necessary to provide Platform services;',
                                'to comply with legal and financial obligations (including HMRC requirements);',
                                'for up to 12 months following account deletion, after which data will be anonymised or securely deleted.'
                            ]} />
                            <Clause>7.2 Anonymised data may be retained indefinitely for analytics and system improvement purposes.</Clause>
                        </Section>

                        {/* 8 */}
                        <Section title="8. Data Security">
                            <Clause>8.1 We implement appropriate technical and organisational measures to protect personal data, including:</Clause>
                            <BulletList items={[
                                'secure servers and encryption;',
                                'access controls and authentication; and',
                                'secure payment processing via Stripe.'
                            ]} />
                            <Clause>8.2 In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will notify the Information Commissioner's Office (ICO) within 72 hours and will notify affected individuals where required.</Clause>
                        </Section>

                        {/* 9 */}
                        <Section title="9. Your Rights Under UK GDPR">
                            <Clause>9.1 You have the right to:</Clause>
                            <BulletList items={[
                                'Access your personal data (Subject Access Request);',
                                'Correct inaccurate or incomplete data;',
                                'Request deletion of your data (right to erasure), subject to legal obligations;',
                                'Restrict or object to processing;',
                                'Request data portability;',
                                'Withdraw consent at any time where processing is based on consent;',
                                'Request human review of automated decisions (see Section 5).'
                            ]} />
                            <Clause>9.2 Requests can be submitted to <Placeholder label="Insert Contact Email" />. We will respond within 30 days. We may request proof of identity before processing requests.</Clause>
                            <Clause>9.3 You have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#8B5CF6] hover:underline">ico.org.uk</a> if you are unhappy with how we handle your personal data.</Clause>
                        </Section>

                        {/* 10 */}
                        <Section title="10. Cookies and Tracking">
                            <Clause>10.1 We use cookies and similar technologies on our Platform. Cookies are small text files stored on your device.</Clause>
                            <SubHeading>Categories of Cookies We Use</SubHeading>
                            <BulletList items={[
                                <><strong>Essential cookies:</strong> necessary for core Platform functionality. These cannot be disabled.</>,
                                <><strong>Analytics cookies:</strong> used to analyse usage and improve the Platform (e.g. page visits, session duration). These are only placed with your consent.</>,
                                <><strong>Preference cookies:</strong> used to remember your settings and preferences.</>,
                                <><strong>Third-party cookies:</strong> Stripe and other service providers may set cookies for payment processing and fraud prevention purposes.</>
                            ]} />
                            <Clause>10.2 In accordance with the Privacy and Electronic Communications Regulations (PECR), we will request your consent before placing non-essential cookies. You may withdraw consent or manage cookie preferences at any time via <Placeholder label="Insert Cookie Preference Link" /> or through your browser settings.</Clause>
                            <Clause>10.3 A full Cookie Policy is available at <Link to="/legal/cookie-policy" className="text-[#8B5CF6] hover:underline">sampler.org.uk/legal/cookie-policy</Link>.</Clause>
                        </Section>

                        {/* 11 */}
                        <Section title="11. International Data Transfers">
                            <Clause>11.1 Where personal data is transferred outside the UK, we ensure appropriate safeguards are in place, such as UK-approved Standard Contractual Clauses, to protect your data to an equivalent standard.</Clause>
                        </Section>

                        {/* 12 */}
                        <Section title="12. Changes to This Policy">
                            <Clause>12.1 We may update this Privacy Policy from time to time. Where changes are material, we will notify users via the Platform or by email prior to the change taking effect.</Clause>
                            <Clause>12.2 Continued use of the Platform following notification of changes constitutes acceptance of the updated policy.</Clause>
                        </Section>

                        {/* 13 */}
                        <Section title="13. Contact and Data Controller">
                            <Clause>13.1 Ritson Portfolio Ltd (trading as Sampler) is the data controller for personal data processed via the Platform.</Clause>
                            <Clause>13.2 For any questions, data requests, or to exercise your rights:</Clause>
                            <div className="ml-4 text-sm space-y-1">
                                <p>Email: <a href="mailto:privacy@sampler.org.uk" className="text-[#8B5CF6] hover:underline">privacy@sampler.org.uk</a></p>
                                <p>Address: Ritson Haven, Ritson Road, London, E8 1DE</p>
                            </div>
                        </Section>

                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Shared sub-components ── */

const Section = ({ title, children }) => (
    <section>
        <h2 className="text-sm font-bold mb-4 text-[#0A0A0A] uppercase tracking-widest" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {title}
        </h2>
        <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </section>
);

const SubHeading = ({ children }) => (
    <h3 className="font-semibold text-slate-800 mt-4 mb-1" style={{ fontFamily: 'Barlow, sans-serif' }}>{children}</h3>
);

const Clause = ({ children }) => (
    <p>{children}</p>
);

const BoldClause = ({ heading, children }) => (
    <p><strong>{heading}</strong> — {children}</p>
);

const BulletList = ({ items }) => (
    <ul className="list-disc ml-5 space-y-1.5">
        {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
);

export default PrivacyPolicy;
